import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as afFilterSelectors from 'data/afFilter/afFilter.selectors';
import { RootState } from 'data/root.reducers';
import * as afFilterActions from 'data/afFilter/afFilter.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { AfFilter } from 'data/afFilter/afFilter.types';
import { FormTemplateSection } from 'data/template/template.types';
import AfFilterForm from 'routes/AfFilter/components/AfFilterForm';
import * as types from 'data/afFilter/afFilter.types';
import { afFilterTemplate } from '../../templates';
import { adaptTemplateVisibilityMode } from '../../utils/afFilterTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.AfFilter
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedAfFilter: afFilterSelectors
    .getSelectedAfFilter(state)[0],
  afFilter: afFilterSelectors.getAfFilter(state),
});

const mapDispatch = {
  goToAfFilterList: afFilterActions.goToAfFilterList,
  getAfFilter: afFilterActions.getAfFilter,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const AfFilterView = ({
  history,
  selectedAfFilter,
  deliveryFunctionId,
  afFilter,
  goToAfFilterList,
  getAfFilter,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<AfFilter>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(afFilterTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedAfFilter?.id;

  useEffect(() => {
    if (id) {
      getAfFilter(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getAfFilter]);

  useEffect(() => {
    if (selectedAfFilter != null && template != null) {
      const clonedAfFilter = cloneDeep(selectedAfFilter);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      setTemplate(clonedTemplate);
      setFormData(clonedAfFilter);
    }
  }, [afFilter, id]);

  const handleCancel = () => goToAfFilterList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AfFilter);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <AfFilterForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (afFilter == null) {
    return <Fragment />;
  }

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('afFilter.view.title'))}
        <div className="read-only">
          {renderForm()}
        </div>
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(AfFilterView));
